import { markRaw } from 'vue';
import { IPassage } from '../../models/Passage';
import { ActivePigRun, PipelineSectionMarker } from '../../models/ActivePigRun';
import {
  PigRunStoreState,
  PigRunMapEntry,
  PipelineSectionMarkerState,
  PigLocationState,
  UpdatePigLocationPayload,
  UpdateMarkerPayload,
  RefreshSinglePigRunPayload,
  LaunchPigRunPayload,
} from '../../models/PigRunState';
import PigRunService from '../../services/PigRunService';
import { VerifiedPassageSource } from '../../models/VerifiedPassageSource';
import { hasPigRunLeftLiveView } from '../../utils/PigRunLiveView';

/** In-flight dedup: if a fetch is already in progress, return the same promise. */
let activePigRunsInflight: Promise<void> | null = null;

const getPigRunPendingIds: Set<number> = new Set();
const getPigRunRefreshPendingIds: Set<number> = new Set();
const getPigRunLastFetchTime: Map<number, number> = new Map();
const GET_PIG_RUN_THROTTLE_MS = 10000;

const verifiedPassageSourcesInflight: Map<number, Promise<void>> = new Map();
const verifiedPassageSourcesRefreshPending: Set<number> = new Set();

/**
 * Builds a PigRunMapEntry from an ActivePigRun API response.
 * Preserves runtime state if an entry already exists in the map.
 */
function buildMapEntry(
  pigRun: ActivePigRun,
  existingEntry?: PigRunMapEntry
): PigRunMapEntry {
  const markers: PipelineSectionMarkerState[] = [...pigRun.markers]
    .sort((a, b) => a.sortOrder - b.sortOrder)
    .map((m: PipelineSectionMarker) => ({
      pipelineSectionMarkerId: m.pipelineSectionMarkerId,
      name: m.name,
      trackState: m.trackState,
      description: m.description,
      pigRunId: m.pigRunId,
      latitude: m.latitude,
      longitude: m.longitude,
      pipelineDistance: m.pipelineDistance,
      sortOrder: m.sortOrder,
      hasVerifiedPassage: m.hasVerifiedPassage ?? false,
      manualLinkDevices: m.manualLinkDevices,
    }));

  return {
    pigRun: markRaw(pigRun),
    markers,
    pigLocation: existingEntry?.pigLocation ?? null,
    verifiedPassageSources: existingEntry?.verifiedPassageSources ?? [],
    verifiedPassageSourcesLoadState:
      existingEntry?.verifiedPassageSourcesLoadState ?? 'idle',
    verifiedPassageSourcesError:
      existingEntry?.verifiedPassageSourcesError ?? null,
  };
}

export default {
  state: {
    pigRuns: {} as Record<number, PigRunMapEntry>,
    visiblePigRunIds: [] as number[],
    missedPassages: [] as IPassage[],
    selectedPigRunId: null as number | null,
    selectedCompletedPigRunId: null as number | null,
    completedTrackingWorksheetRefreshVersions: {} as Record<number, number>,
    isReportLoading: false,
    isRCompRunSynching: false,
    showMLPanel: false,
  } as PigRunStoreState,
  mutations: {
    // ── Active pig run map mutations ────────────────────────────

    /** Bulk-load all active pig runs into the map (initial fetch / full refresh). */
    SET_PIG_RUNS(state: PigRunStoreState, payload: ActivePigRun[]) {
      const newMap: Record<number, PigRunMapEntry> = {};
      for (const pigRun of payload) {
        newMap[pigRun.pigRunId] = buildMapEntry(
          pigRun,
          state.pigRuns[pigRun.pigRunId]
        );
      }
      state.pigRuns = newMap;
      state.visiblePigRunIds = payload.map((pr) => pr.pigRunId);
    },

    /** Upsert a single pig run (after single-run API refresh). */
    SET_PIG_RUN(state: PigRunStoreState, pigRun: ActivePigRun) {
      state.pigRuns = {
        ...state.pigRuns,
        [pigRun.pigRunId]: buildMapEntry(
          pigRun,
          state.pigRuns[pigRun.pigRunId]
        ),
      };
      if (!state.visiblePigRunIds.includes(pigRun.pigRunId)) {
        state.visiblePigRunIds = [...state.visiblePigRunIds, pigRun.pigRunId];
      }
    },

    /** Remove a cancelled or completed pig run from the active map and the list. */
    REMOVE_ACTIVE_PIG_RUN(state: PigRunStoreState, pigRunId: number) {
      const { [pigRunId]: _, ...rest } = state.pigRuns;
      state.pigRuns = rest;
      state.visiblePigRunIds = state.visiblePigRunIds.filter(
        (id) => id !== pigRunId
      );
      if (state.selectedPigRunId === pigRunId) {
        state.selectedPigRunId = null;
      }
    },

    /** Update pig lat/lon/speed/ETA for a single run (high-frequency SignalR). */
    UPDATE_PIG_LOCATION(
      state: PigRunStoreState,
      payload: UpdatePigLocationPayload
    ) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      entry.pigLocation = {
        lat: payload.lat,
        lon: payload.lon,
        speed: payload.speed,
        serverTime: payload.serverTime,
        etaToNext: payload.etaToNext,
        etaToNextTrack: payload.etaToNextTrack,
        etaToEnd: payload.etaToEnd,
      };
    },

    /** Partial update of a marker's fields (state change, description change, etc.). */
    UPDATE_MARKER(state: PigRunStoreState, payload: UpdateMarkerPayload) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      const idx = entry.markers.findIndex(
        (m) => m.pipelineSectionMarkerId === payload.pipelineSectionMarkerId
      );
      if (idx !== -1) {
        entry.markers[idx] = { ...entry.markers[idx], ...payload.fields };
        entry.markers = [...entry.markers];
        state.pigRuns = { ...state.pigRuns };
      }
    },

    /** Update the hasVerifiedPassage flag for a specific marker (after passage verification). */
    UPDATE_MARKER_VERIFICATION(
      state: PigRunStoreState,
      payload: {
        pigRunId: number;
        pipelineSectionMarkerId: number;
        hasVerifiedPassage: boolean;
      }
    ) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      const idx = entry.markers.findIndex(
        (m) => m.pipelineSectionMarkerId === payload.pipelineSectionMarkerId
      );
      if (idx !== -1) {
        entry.markers[idx] = {
          ...entry.markers[idx],
          hasVerifiedPassage: payload.hasVerifiedPassage,
        };
        entry.markers = [...entry.markers];
        state.pigRuns = { ...state.pigRuns };
      }
    },

    SET_VERIFIED_PASSAGE_SOURCES_LOADING(
      state: PigRunStoreState,
      pigRunId: number
    ) {
      const entry = state.pigRuns[pigRunId];
      if (!entry) return;

      entry.verifiedPassageSourcesLoadState = 'loading';
      entry.verifiedPassageSourcesError = null;
    },

    SET_VERIFIED_PASSAGE_SOURCES(
      state: PigRunStoreState,
      payload: {
        pigRunId: number;
        sources: VerifiedPassageSource[];
      }
    ) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      entry.verifiedPassageSources = payload.sources;
      entry.verifiedPassageSourcesLoadState = 'loaded';
      entry.verifiedPassageSourcesError = null;
    },

    SET_VERIFIED_PASSAGE_SOURCES_ERROR(
      state: PigRunStoreState,
      payload: { pigRunId: number; error: string }
    ) {
      const entry = state.pigRuns[payload.pigRunId];
      if (!entry) return;

      entry.verifiedPassageSourcesLoadState = 'error';
      entry.verifiedPassageSourcesError = payload.error;
    },

    /** Control which pig runs are rendered on the map. */
    SET_VISIBLE_PIG_RUN_IDS(state: PigRunStoreState, ids: number[]) {
      state.visiblePigRunIds = ids;
    },

    /** Insert a newly launched live-track pig run from the launch API response. */
    LAUNCH_PIG_RUN(state: PigRunStoreState, payload: LaunchPigRunPayload) {
      const { response, toolName, toolType, launchDate } = payload;

      const markers: PipelineSectionMarkerState[] = [...response.markers]
        .sort((a, b) => a.sortOrder - b.sortOrder)
        .map((m) => ({
          pipelineSectionMarkerId: m.pipelineSectionMarkerId,
          name: m.name,
          trackState: m.trackState,
          description: m.description,
          pigRunId: response.pigRunId,
          latitude: m.latitude,
          longitude: m.longitude,
          pipelineDistance: m.pipelineDistance,
          sortOrder: m.sortOrder,
          hasVerifiedPassage: false,
          manualLinkDevices: [],
        }));

      const pigRun: ActivePigRun = {
        pigRunId: response.pigRunId,
        pipelineSectionConfigurationId: 0,
        type: 'LT',
        lifecycleState: 'A',
        name: toolName,
        launchDate,
        jobNumber: '',
        expectedSpeed: 0,
        isArchived: false,
        markers: response.markers as any,
        devices: [],
        tools: [],
      };

      const entry: PigRunMapEntry = {
        pigRun,
        markers,
        pigLocation: null,
        verifiedPassageSources: [],
        verifiedPassageSourcesLoadState: 'idle',
        verifiedPassageSourcesError: null,
      };

      state.pigRuns = { ...state.pigRuns, [response.pigRunId]: entry };
      if (!state.visiblePigRunIds.includes(response.pigRunId)) {
        state.visiblePigRunIds = [...state.visiblePigRunIds, response.pigRunId];
      }
    },

    SET_MISSED_PASSAGES(state: PigRunStoreState, payload: IPassage[]) {
      state.missedPassages = payload;
    },
    CLEAR_MISSED_PASSAGES(state: PigRunStoreState) {
      state.missedPassages = [];
    },
    REMOVE_MISSED_PASSAGE(state: PigRunStoreState, payload: IPassage) {
      state.missedPassages = state.missedPassages.filter(
        (p: IPassage) => p.passageId !== payload.passageId
      );
    },
    SET_SELECTED_PIG_RUN_ID(state: PigRunStoreState, pigRunId: number | null) {
      // if pigRunId is null, find the next active pig run from the active map
      if (pigRunId === null) {
        const activeIds = Object.keys(state.pigRuns).map(Number);
        state.selectedPigRunId = activeIds.length > 0 ? activeIds[0] : null;
        return;
      }

      state.selectedPigRunId = pigRunId;
      state.selectedCompletedPigRunId = null;
    },
    SET_SELECTED_COMPLETED_PIG_RUN_ID(
      state: PigRunStoreState,
      pigRunId: number | null
    ) {
      state.selectedCompletedPigRunId = pigRunId;
    },
    REQUEST_COMPLETED_TRACKING_WORKSHEET_REFRESH(
      state: PigRunStoreState,
      pigRunId: number
    ) {
      if (!Number.isInteger(pigRunId) || pigRunId <= 0) return;

      const refreshVersions =
        state.completedTrackingWorksheetRefreshVersions ?? {};
      state.completedTrackingWorksheetRefreshVersions = {
        ...refreshVersions,
        [pigRunId]: (refreshVersions[pigRunId] ?? 0) + 1,
      };
    },
    CLEAR_SELECTED_PIG_RUN_ID(state: PigRunStoreState) {
      state.selectedPigRunId = null;
    },
    SET_REPORT_LOADING_STATUS(
      state: PigRunStoreState,
      isReportLoading: boolean
    ) {
      state.isReportLoading = isReportLoading;
    },
    SET_REPORT_SYNCH_STATUS(
      state: PigRunStoreState,
      isRCompRunSynching: boolean
    ) {
      state.isRCompRunSynching = isRCompRunSynching;
    },
    SET_SHOW_ML_PANEL(state: PigRunStoreState, show: boolean) {
      state.showMLPanel = show;
    },
  },
  getters: {
    /** Returns active pig runs as a flat array for backward compatibility. */
    activePigRunsList(state: PigRunStoreState): ActivePigRun[] {
      return Object.values(state.pigRuns).map((entry) => entry.pigRun);
    },
  },
  actions: {
    async getActivePigRuns(context: {
      state: PigRunStoreState;
      commit: (type: string, payload?: unknown) => void;
      dispatch: (type: string, payload?: unknown) => Promise<unknown>;
    }) {
      if (activePigRunsInflight) return activePigRunsInflight;
      activePigRunsInflight = (async () => {
        try {
          const response = await PigRunService.getActivePigRuns();
          const activePigRuns: ActivePigRun[] = response.data;
          context.commit('SET_PIG_RUNS', activePigRuns);
          Promise.all(
            activePigRuns
              .map((pigRun) => pigRun.pigRunId)
              .filter(
                (pigRunId) =>
                  context.state.pigRuns[pigRunId]
                    ?.verifiedPassageSourcesLoadState !== 'idle'
              )
              .map((pigRunId) =>
                context.dispatch('refreshVerifiedPassageSources', pigRunId)
              )
          ).catch((error) => {
            console.error(
              'Failed to refresh verified passage sources after active pig run refresh:',
              error
            );
          });
        } catch (error) {
          console.error('Failed to fetch active pig runs:', error);
          context.commit('SET_PIG_RUNS', []);
        } finally {
          activePigRunsInflight = null;
        }
      })();
      return activePigRunsInflight;
    },
    async refreshSinglePigRun(
      context: {
        state: PigRunStoreState;
        commit: (type: string, payload?: unknown) => void;
        dispatch: (type: string, payload?: unknown) => Promise<unknown>;
      },
      payload: number | RefreshSinglePigRunPayload
    ) {
      const { pigRunId, force = false } =
        typeof payload === 'number' ? { pigRunId: payload } : payload;

      if (getPigRunPendingIds.has(pigRunId)) {
        if (force) {
          getPigRunRefreshPendingIds.add(pigRunId);
        }
        return;
      }
      const lastFetch = getPigRunLastFetchTime.get(pigRunId) ?? 0;
      if (!force && Date.now() - lastFetch < GET_PIG_RUN_THROTTLE_MS) return;

      const entryBeforeRefresh = context.state.pigRuns[pigRunId];
      if (!entryBeforeRefresh) return;

      getPigRunPendingIds.add(pigRunId);
      try {
        const response = await PigRunService.getActivePigRun(pigRunId);
        if (context.state.pigRuns[pigRunId] !== entryBeforeRefresh) return;

        // EN-1558: another user ended or cancelled this run, so it leaves the live view.
        if (hasPigRunLeftLiveView(response.data)) {
          context.commit('REMOVE_ACTIVE_PIG_RUN', pigRunId);
          getPigRunLastFetchTime.delete(pigRunId);
          return;
        }

        context.commit('SET_PIG_RUN', response.data);
        getPigRunLastFetchTime.set(pigRunId, Date.now());
        const entry = context.state.pigRuns[pigRunId];
        if (entry && entry.verifiedPassageSourcesLoadState !== 'idle') {
          context.dispatch('refreshVerifiedPassageSources', pigRunId);
        }
      } catch (error) {
        const status = (error as { response?: { status?: number } }).response
          ?.status;
        if (
          status === 404 &&
          context.state.pigRuns[pigRunId] === entryBeforeRefresh
        ) {
          context.commit('REMOVE_ACTIVE_PIG_RUN', pigRunId);
          getPigRunLastFetchTime.delete(pigRunId);
        }
        console.error(`Failed to refresh pig run ${pigRunId}:`, error);
      } finally {
        getPigRunPendingIds.delete(pigRunId);
        if (getPigRunRefreshPendingIds.delete(pigRunId)) {
          context.dispatch('refreshSinglePigRun', { pigRunId, force: true });
        }
      }
    },
    refreshVerifiedPassageSources(
      context: {
        state: PigRunStoreState;
        dispatch: (type: string, payload?: unknown) => Promise<unknown>;
      },
      pigRunId: number
    ) {
      const entry = context.state.pigRuns[pigRunId];
      if (!entry || entry.verifiedPassageSourcesLoadState === 'idle') {
        return Promise.resolve();
      }

      return context.dispatch('loadVerifiedPassageSources', {
        pigRunId,
        force: true,
      });
    },
    loadVerifiedPassageSources(
      context: {
        state: PigRunStoreState;
        commit: (type: string, payload?: unknown) => void;
      },
      payload: { pigRunId: number; force?: boolean }
    ) {
      const { pigRunId, force = false } = payload;
      const entry = context.state.pigRuns[pigRunId];
      if (!entry) {
        return Promise.resolve();
      }

      const inflight = verifiedPassageSourcesInflight.get(pigRunId);
      if (inflight) {
        if (force) {
          verifiedPassageSourcesRefreshPending.add(pigRunId);
        }
        return inflight;
      }

      if (!force && entry.verifiedPassageSourcesLoadState === 'loaded') {
        return Promise.resolve();
      }

      const request = (async () => {
        do {
          verifiedPassageSourcesRefreshPending.delete(pigRunId);
          if (!context.state.pigRuns[pigRunId]) return;

          context.commit('SET_VERIFIED_PASSAGE_SOURCES_LOADING', pigRunId);
          try {
            const response = await PigRunService.getVerifiedPassageSources(
              pigRunId
            );
            context.commit('SET_VERIFIED_PASSAGE_SOURCES', {
              pigRunId,
              sources: response.data,
            });
          } catch (error) {
            console.error(
              `Failed to fetch verified passage sources for pig run ${pigRunId}:`,
              error
            );
            context.commit('SET_VERIFIED_PASSAGE_SOURCES_ERROR', {
              pigRunId,
              error: 'Failed to load verified passage sources.',
            });
          }
        } while (verifiedPassageSourcesRefreshPending.has(pigRunId));
      })();

      const trackedRequest = request.finally(() => {
        if (verifiedPassageSourcesInflight.get(pigRunId) === trackedRequest) {
          verifiedPassageSourcesInflight.delete(pigRunId);
          verifiedPassageSourcesRefreshPending.delete(pigRunId);
        }
      });
      verifiedPassageSourcesInflight.set(pigRunId, trackedRequest);
      return trackedRequest;
    },
    showPigRunManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_PIG_RUN_MANAGER', payload);
    },
    hidePigRunManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('HIDE_PIG_RUN_MANAGER', payload);
    },
    showReportManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_REPORT_MANAGER', payload);
    },
    showDownloadManager(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_DOWNLOAD_MANAGER', payload);
    },
    showBroadcastCommands(
      context: { commit: (type: string, payload?: unknown) => void },
      payload: unknown
    ) {
      context.commit('SHOW_BROADCAST_COMMANDS', payload);
    },
    removeMissedPassage(
      context: { commit: (type: string, payload?: unknown) => void },
      passage: IPassage
    ) {
      context.commit('REMOVE_MISSED_PASSAGE', passage);
    },

    async SynchFinishedPigRun(
      context: {
        commit: (type: string, payload?: unknown) => void;
        dispatch: (type: string, payload?: unknown) => void;
      },
      pigRunId: number
    ) {
      // Status tracking only — list refresh is handled locally in PigRunManager
      context.commit('SET_REPORT_SYNCH_STATUS', true);
      try {
        await PigRunService.synchFinishedPigRun(pigRunId);
      } catch (error) {
        console.error('Failed to sync pig run:', error);
      } finally {
        context.commit('SET_REPORT_SYNCH_STATUS', false);
      }
    },

    // Event bus actions — no state/mutations, used to subscribe to actions in other components
    addManualMarkerPassage(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    addMarkerNote(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    changeMarkerDescription(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    changeMarkerState(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    assignMarkerUid(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    unlinkMarkerUid(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    calculateMarkerEta(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    refreshMarkerEta(_context: unknown, _markerEtas: number) {},
    verifyMarkerPassage(
      _context: unknown,
      _payload: { pigRunId: number; pipelineSectionMarkerId: number }
    ) {},
    refreshPigRun(_context: unknown, _runId: number) {},
    refreshcompPigRun(_context: unknown, _runId: number) {},
    pigRunsReady(_context: unknown, _runId: number) {},
    updatePigLoc(_context: unknown, _info: unknown) {},
    updatePigLocV2(_context: unknown, _info: unknown) {},
    passRunId(_context: unknown, _runId: number) {},

    pigProgressMaximizeRequest(_context: unknown, _runId: number) {},
    pigProgressMinimizeRequest(_context: unknown, _runId: number) {},
  },
};
